import React, { useId, useMemo, useState } from 'react';
import { ChevronDown, Leaf } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { calendarRowsForPack, monthLabel } from './seasonCalendarModel.js';

const ORIGIN_KEYS = {
  peru: ['map.scorecards.seasonCalendarOriginPeru', 'Peru'],
  chile: ['map.scorecards.seasonCalendarOriginChile', 'Chile'],
};

const DOT_SIZE = {
  light: 'h-1.5 w-1.5 bg-bloom-green/40',
  on: 'h-2 w-2 bg-bloom-green',
  peak: 'h-3 w-3 bg-bloom-green',
};

function LevelMark({ level, collapsed }) {
  if (level === 'off') return null;
  if (collapsed) {
    return <span aria-hidden="true" className={`block rounded-full ${DOT_SIZE[level]}`} />;
  }
  return (
    <Leaf
      size={14}
      aria-hidden="true"
      className={level === 'light' ? 'text-bloom-green/40' : `text-bloom-green ${level === 'peak' ? 'fill-current' : ''}`}
    />
  );
}

function SeasonCalendar({ seasonCalendar }) {
  const { t, i18n } = useTranslation();
  const [expanded, setExpanded] = useState(true);
  const uid = useId();
  const gridId = `${uid}-grid`;
  const locale = i18n?.resolvedLanguage || i18n?.language || 'en';

  const model = useMemo(() => calendarRowsForPack(seasonCalendar), [seasonCalendar]);
  if (!model) return null;

  const levelText = {
    off: t('map.scorecards.seasonCalendarOff', 'no supply'),
    light: t('map.scorecards.seasonCalendarLight', 'Light'),
    on: t('map.scorecards.seasonCalendarOn', 'In season'),
    peak: t('map.scorecards.seasonCalendarPeak', 'Peak'),
  };
  const originName = (o) => {
    const known = ORIGIN_KEYS[o.key];
    return known ? t(known[0], known[1]) : o.label;
  };

  const rows = expanded
    ? model.origins.map((o) => ({ key: o.key, label: originName(o), cells: o.cells }))
    : [{ key: 'combined', label: t('map.scorecards.seasonCalendarCombined', 'All origins'), cells: model.combined }];

  const cellLabel = (rowLabel, c) =>
    t('map.scorecards.seasonCalendarCellLabel', '{{origin}}, {{month}}: {{level}}', {
      origin: rowLabel,
      month: monthLabel(c.m, locale, 'long'),
      level: levelText[c.level],
    }) + (c.isCurrent ? ` (${t('map.scorecards.seasonCalendarCurrentMonth', 'current month')})` : '');

  const rowBorder = 'border-b border-dashed border-[#E8EBE9] dark:border-[#30363d]';

  return (
    <section aria-labelledby={`${uid}-title`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <h3
            id={`${uid}-title`}
            className="font-['Inter'] text-[12px] font-semibold text-[#282A29] dark:text-[#f0f5f2]"
          >
            {t('map.scorecards.seasonCalendarTitle', 'Supply calendar')}
          </h3>
        </div>
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          aria-expanded={expanded}
          aria-controls={gridId}
          aria-label={
            expanded
              ? t('map.scorecards.seasonCalendarHide', 'Hide calendar details')
              : t('map.scorecards.seasonCalendarShow', 'Show calendar details')
          }
          className="-mr-2 -mt-2 inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-md text-[#828382] hover:bg-[#F0F5F2] dark:hover:bg-[#30363d]"
        >
          <ChevronDown size={16} aria-hidden="true" className={`transition-transform ${expanded ? 'rotate-180' : ''}`} />
        </button>
      </div>

      <div id={gridId} role="table" aria-label={t('map.scorecards.seasonCalendarTitle', 'Supply calendar')} className="mt-1 font-['Inter']">
        <div role="row" className="flex flex-col lg:flex-row lg:items-end">
          <div role="columnheader" className="sr-only lg:not-sr-only lg:w-16 lg:shrink-0">
            <span className="sr-only">{t('map.scorecards.seasonCalendarOrigin', 'Origin')}</span>
          </div>
          <div role="none" className="grid min-w-0 flex-1 grid-cols-12 text-center text-[10px] text-[#828382]">
            {model.months.map((m) => {
              const current = m === model.currentMonth;
              return (
                <div
                  key={m}
                  role="columnheader"
                  aria-label={monthLabel(m, locale, 'long')}
                  className={`mx-px rounded-sm py-0.5 ${
                    current ? 'bg-bloom-green/10 font-semibold text-[#282A29] ring-1 ring-bloom-green dark:text-[#f0f5f2]' : ''
                  }`}
                >
                  {monthLabel(m, locale)}
                </div>
              );
            })}
          </div>
        </div>

        {rows.map((row) => (
          <div key={row.key} role="row" className={`flex flex-col py-1 lg:flex-row lg:items-center ${rowBorder}`}>
            <div
              role="rowheader"
              className="py-0.5 text-[11px] font-medium text-[#5d625f] lg:w-16 lg:shrink-0 dark:text-[#a8b0ab]"
            >
              {row.label}
            </div>
            <div role="none" className="grid min-w-0 flex-1 grid-cols-12">
              {row.cells.map((c) => (
                <div
                  key={c.m}
                  role="cell"
                  aria-label={cellLabel(row.label, c)}
                  className={`mx-px flex items-center justify-center rounded-sm ${expanded ? 'h-7' : 'h-5'} ${
                    c.isCurrent ? 'bg-bloom-green/10' : ''
                  }`}
                >
                  <LevelMark level={c.level} collapsed={!expanded} />
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      <ul className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-[#828382]" aria-label={t('map.scorecards.seasonCalendarLegend', 'Legend')}>
        {['light', 'on', 'peak'].map((lvl) => (
          <li
            key={lvl}
            className="inline-flex items-center gap-1"
            title={lvl === 'light' ? t('map.scorecards.seasonCalendarLightHint', 'Includes nominal availability; peaks reflect historical volume.') : undefined}
          >
            <span className="inline-flex h-4 w-4 items-center justify-center">
              <LevelMark level={lvl} collapsed={!expanded} />
            </span>
            {levelText[lvl]}
          </li>
        ))}
      </ul>
    </section>
  );
}

export default SeasonCalendar;
